import React, { useMemo } from 'react';
import { CardDefault, Text } from '@spark';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import './QualityMetrics.css';

const COMPARISON_COLOR = '#FF9800';
const CURRENT_COLOR = 'var(--Semantic-Content-brand, #4f46e5)';

const MetricsChart = ({
    metrics,
    showComparison,
    old_model,
    teacherModelProcessing,
    teacherModelFailed = false,
    studentModelProcessing,
    compareWith,
    currentVersion
}) => {
    const getModelDisplayNames = () => {
        const isTeacherModel = compareWith === 'oldModel';
        const comparisonName = old_model || '';
        const currentName = `SpecificAI ${currentVersion}`;
        const comparisonColor = isTeacherModel ? COMPARISON_COLOR : '#8395a7';
        const currentColor = CURRENT_COLOR;
        return { comparisonName, currentName, comparisonColor, currentColor };
    };

    const { comparisonName, currentName, comparisonColor, currentColor } = getModelDisplayNames();
    const isTeacherComparison = compareWith === 'oldModel';
    const showComparisonProcessing = teacherModelProcessing && isTeacherComparison;
    const showComparisonFailed = teacherModelFailed && isTeacherComparison;
    const showCurrentProcessing = studentModelProcessing;

    const metricsData = useMemo(() => {
        return Object.values(metrics);
    }, [metrics]);

    return (
        <CardDefault
            className="fs-unmask"
            type="collapsed"
            open
            fullWidth
            title="Quality Metrics"
            showSubtitle={false}
            border="yes"
        >
            <div className="quality-metrics-card-body">
            <div style={{ height: 400, backgroundColor: 'var(--Semantic-Bg-secondary, #fafafa)', borderBottom: '2px solid var(--Semantic-Border-default, #e2e2e9)' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metricsData} barGap={showComparison ? 4 : 0} margin={{ top: 70, right: 0, left: -30, bottom: -30 }}>
                        <CartesianGrid stroke="#f0f0f0" strokeWidth={2} vertical={false} />
                        <XAxis
                            dataKey="metric"
                            axisLine={false}
                            tickLine={false}
                            tick={false}
                        />
                        <YAxis
                            domain={[0, 1]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#666' }}
                            tickFormatter={(value) => value.toFixed(1) * 100}
                        />
                        <RechartsTooltip
                            cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                            contentStyle={{
                                borderRadius: '4px',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                            }}
                            formatter={(value) => [value.toFixed(3), 'Score']}
                        />
                        {showComparison ? (
                            <>
                                <Bar
                                    dataKey="oldModel"
                                    name={comparisonName}
                                    fill={comparisonColor}
                                    radius={[0, 0, 0, 0]}
                                    isAnimationActive
                                    animationDuration={800}
                                    animationEasing="ease-out"
                                    minPointSize={2}
                                    barSize={40}
                                    background={{ fill: '#ebebeb' }}
                                />
                                <Bar
                                    dataKey="newModel"
                                    name={currentName}
                                    fill={currentColor}
                                    radius={[0, 0, 0, 0]}
                                    isAnimationActive
                                    animationDuration={800}
                                    animationEasing="ease-out"
                                    animationBegin={100}
                                    minPointSize={2}
                                    barSize={40}
                                    background={{ fill: '#ebebeb' }}
                                />
                            </>
                        ) : (
                            <Bar
                                dataKey="newModel"
                                name={currentName}
                                fill={currentColor}
                                radius={[4, 4, 0, 0]}
                                isAnimationActive
                                animationDuration={800}
                                animationEasing="ease-out"
                                minPointSize={2}
                                barSize={40}
                                background={{ fill: '#ebebeb' }}
                            />
                        )}
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                margin: '0 30px',
                marginTop: 8,
            }}>
                {metricsData.map(({ metric }, idx) => (
                    <div key={idx} style={{ flex: 1, textAlign: 'center', marginLeft: 66 }}>
                        <Text variant="base-medium" color="secondary">{metric}</Text>
                    </div>
                ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, gap: 20 }}>
                {comparisonName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 12, height: 12, backgroundColor: comparisonColor, borderRadius: 2 }} />
                        <Text variant="base-regular">
                            {comparisonName}
                            {showComparisonProcessing && (
                                <Text variant="detail-regular" color="secondary" as="span"> (Processing)</Text>
                            )}
                            {showComparisonFailed && (
                                <Text variant="detail-regular" as="span" style={{ color: 'var(--Semantic-Content-error, #dc2626)' }}> (Failed)</Text>
                            )}
                        </Text>
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{ width: 12, height: 12, backgroundColor: currentColor, borderRadius: 2 }} />
                    <Text variant="base-regular">
                        {currentName}
                        {showCurrentProcessing && (
                            <Text variant="detail-regular" color="secondary" as="span"> (Processing)</Text>
                        )}
                    </Text>
                </div>
            </div>
            </div>
        </CardDefault>
    );
};

export default MetricsChart;
